import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SEO from '../../components/SEO';
import FAQ from '../../components/ui/FAQ';
import { dataScienceFAQs } from '../../data/faqs';
import { sendRequirementsFormEmail } from '../../utils/emailService';
import { scrollToElement } from '../../utils/scrollUtils';
import { useContactInfo } from '../../utils/contactObfuscation';

const DataScienceLanding = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: 'Data Science',
    requirements: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const contactInfo = useContactInfo();

  const seoData = {
    title: 'Data Science & Analytics Services - Business Intelligence | OctalChip',
    description: 'Comprehensive data science services including data analysis, statistical modeling, and business intelligence. Turn data into actionable business insights.',
    keywords: 'data science, data analytics, business intelligence, statistical modeling, data visualization, data mining',
    canonical: 'https://www.octalchip.com/landing/data-science',
    faqData: dataScienceFAQs,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Data Science & Analytics Services",
      "description": "Comprehensive data science services for business intelligence",
      "provider": {
        "@type": "Organization",
        "name": "OctalChip",
        "url": "https://www.octalchip.com"
      }
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const result = await sendRequirementsFormEmail(formData);
      
      if (result.success) {
        setSubmitMessage('Thank you! Your requirements have been submitted successfully. We\'ll get back to you within 24 hours with a detailed quote.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          projectType: 'Data Science',
          requirements: ''
        });
      } else {
        setSubmitMessage('Sorry, there was an error submitting your requirements. Please try again or contact us directly.');
      }
    } catch (error) {
      setSubmitMessage('Sorry, there was an error submitting your requirements. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: 'BarChart',
      title: 'Data Analysis',
      description: 'Comprehensive analysis to uncover insights and patterns'
    },
    {
      icon: 'TrendingUp',
      title: 'Statistical Modeling',
      description: 'Advanced models for prediction and inference'
    },
    {
      icon: 'PieChart',
      title: 'Data Visualization',
      description: 'Interactive dashboards for better understanding'
    },
    {
      icon: 'Briefcase',
      title: 'Business Intelligence',
      description: 'Transform data into actionable business insights'
    }
  ];

  const technologies = [
    { name: 'Python', description: 'Primary data science language' },
    { name: 'R', description: 'Statistical computing' },
    { name: 'SQL', description: 'Database queries' },
    { name: 'Tableau', description: 'Data visualization' },
    { name: 'Power BI', description: 'Business intelligence' },
    { name: 'Jupyter', description: 'Interactive notebooks' }
  ];

  const useCases = [
    {
      title: 'Business Intelligence',
      description: 'Transform data into actionable business insights',
      icon: 'Briefcase'
    },
    {
      title: 'Market Research',
      description: 'Analyze market trends and customer behavior',
      icon: 'TrendingUp'
    },
    {
      title: 'Customer Analytics',
      description: 'Understand customer preferences and behavior',
      icon: 'Users'
    },
    {
      title: 'Performance Dashboards',
      description: 'Real-time dashboards for monitoring KPIs',
      icon: 'Monitor'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        faqData={seoData.faqData}
        structuredData={seoData.structuredData}
      />
      <Header />
      
      {/* Hero Section */}
      <section id="hero" className="py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-br from-purple-50 via-white to-indigo-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="text-center lg:text-left">
                <div className="inline-block bg-[#00d690]/10 text-[#00d690] px-4 py-2 rounded-full text-sm font-medium mb-6">
                  Data Science
                </div>
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-gray-800">
                  World's Best Data Science Services - Turn Data Into Insights Globally
                </h1>
                <p className="text-lg md:text-xl mb-8 text-gray-600 leading-relaxed">
                  Trusted by businesses worldwide, we provide comprehensive data science services including data analysis, statistical modeling, and business intelligence. Get insights that drive better decisions and optimize operations - recognized globally for excellence.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
                  <Button
                    variant="primary"
                    animation="glow"
                    className="px-8 py-4 text-base md:text-lg bg-[#00d690] text-white hover:bg-[#00b876] shadow-lg"
                    onClick={async () => {
                      await scrollToElement('#get-quote', { 
                        smooth: true,
                        maxRetries: 15,
                        retryDelay: 150
                      }).catch(() => {});
                    }}
                  >
                    Get Free Quote
                  </Button>
                  <Button
                    variant="outline"
                    animation="slide"
                    className="px-8 py-4 text-base md:text-lg border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white"
                    onClick={() => navigate('/services/ai-ml-services/data-science')}
                  >
                    View Full Service
                  </Button>
                </div>

                <div className="flex flex-wrap gap-6 text-sm justify-center lg:justify-start">
                  <div className="flex items-center">
                    <Icon name="Check" size={20} className="text-[#00d690] mr-2" />
                    <span className="text-gray-700">Data Analysis</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="Check" size={20} className="text-[#00d690] mr-2" />
                    <span className="text-gray-700">Statistical Modeling</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="Check" size={20} className="text-[#00d690] mr-2" />
                    <span className="text-gray-700">Business Intelligence</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-xl border border-gray-100">
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="MessageSquare" size={32} className="text-[#00d690]" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-800">Get Your Free Quote</h3>
                  <p className="text-gray-600 mb-6">Tell us about your data science project and get a detailed estimate in 24 hours</p>
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <input 
                      type="text" 
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Your Name"
                      required
                    />
                    <input 
                      type="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Your Email"
                      required
                    />
                    <input 
                      type="tel" 
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Your Phone"
                      required
                    />
                    <textarea 
                      name="requirements"
                      value={formData.requirements}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Describe your data science project..."
                      required
                    ></textarea>
                    
                    {submitMessage && (
                      <div className={`p-4 rounded-lg text-center ${
                        submitMessage.includes('Thank you') 
                          ? 'bg-green-100 text-green-700 border border-green-200' 
                          : 'bg-[#00d690]/10 text-red-700 border border-red-200'
                      }`}>
                        {submitMessage}
                      </div>
                    )}
                    
                    <Button 
                      type="submit"
                      variant="primary"
                      animation="glow"
                      className="w-full py-3 bg-[#00d690] text-white hover:bg-[#00b876] shadow-lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Get Free Quote'}
                    </Button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#00d690] mb-2">22+</div>
              <div className="text-sm md:text-base text-gray-600">Data Science Projects</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#00d690] mb-2">85%</div>
              <div className="text-sm md:text-base text-gray-600">Insight Accuracy</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#00d690] mb-2">150%</div>
              <div className="text-sm md:text-base text-gray-600">ROI Increase</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#00d690] mb-2">24/7</div>
              <div className="text-sm md:text-base text-gray-600">Support Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Why Choose Our Data Science Services?</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              We deliver data science solutions that provide actionable insights
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-xl hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={benefit.icon} size={32} className="text-[#00d690]" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section id="technologies" className="py-12 sm:py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Our Data Science Technology Stack</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              We use the latest data science tools and platforms
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {technologies.map((tech, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow text-center">
                <h3 className="font-bold text-gray-800 mb-2">{tech.name}</h3>
                <p className="text-sm text-gray-600">{tech.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">What We Build</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              From business intelligence to customer analytics, we build data science solutions for every need
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-gray-50 p-6 md:p-8 rounded-xl hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name={useCase.icon} size={24} className="text-[#00d690]" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{useCase.title}</h3>
                <p className="text-gray-600">{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section id="pain-points" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Common Data Science Challenges We Solve</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              We understand the pain points businesses face. Here's how we help solve them
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Data Silos & Fragmentation</h3>
                  <p className="text-gray-600">Data scattered across systems prevents insights. We integrate and consolidate data from all sources creating unified data warehouses for comprehensive analysis.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Poor Data Quality</h3>
                  <p className="text-gray-600">Dirty, incomplete data leads to wrong insights. We clean, validate, and enrich data ensuring high-quality datasets for accurate analysis and modeling.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Lack of Actionable Insights</h3>
                  <p className="text-gray-600">Raw data doesn't drive decisions. We analyze data, build statistical models, and create visualizations providing actionable insights that improve business outcomes.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Slow Analysis Processes</h3>
                  <p className="text-gray-600">Manual analysis takes weeks delaying decisions. We automate analysis pipelines and build dashboards providing real-time insights for faster decision-making.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Limited Statistical Expertise</h3>
                  <p className="text-gray-600">Lack of statistical knowledge leads to incorrect conclusions. We provide expert data scientists building robust models with proper statistical validation ensuring reliable results.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Data Privacy & Compliance</h3>
                  <p className="text-gray-600">Handling sensitive data requires strict compliance. We implement GDPR, HIPAA, and other compliance frameworks ensuring data privacy and regulatory compliance in all data science projects.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-12 sm:py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Our Data Science Process</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              A proven methodology that ensures quality, transparency, and on-time delivery
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#00d690] text-white rounded-full flex items-center justify-center font-bold text-lg mb-4">
                01
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Data Collection</h3>
              <p className="text-gray-600">Collect and organize data from multiple sources</p>
            </div>
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#00d690] text-white rounded-full flex items-center justify-center font-bold text-lg mb-4">
                02
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Analysis & Modeling</h3>
              <p className="text-gray-600">Analyze data and build statistical models</p>
            </div>
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#00d690] text-white rounded-full flex items-center justify-center font-bold text-lg mb-4">
                03
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Visualization & Insights</h3>
              <p className="text-gray-600">Create visualizations and extract insights</p>
            </div>
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#00d690] text-white rounded-full flex items-center justify-center font-bold text-lg mb-4">
                04
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Reporting & Deployment</h3>
              <p className="text-gray-600">Generate reports and deploy solutions</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Client Success Stories</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Icon key={i} name="Star" size={20} className="text-yellow-400 mr-1" />
                ))}
              </div>
              <p className="text-gray-600 mb-4 italic leading-relaxed">"OctalChip's data science team transformed our business intelligence. The insights they provided helped us make data-driven decisions and increase revenue by 150%."</p>
              <div>
                <p className="font-semibold text-gray-800">Emily Chen</p>
                <p className="text-sm text-gray-500">Digital Ventures</p>
              </div>
            </div>
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Icon key={i} name="Star" size={20} className="text-yellow-400 mr-1" />
                ))}
              </div>
              <p className="text-gray-600 mb-4 italic leading-relaxed">"The customer analytics dashboard they built gives us real-time insights. We can now understand customer behavior and optimize our marketing strategies effectively."</p>
              <div>
                <p className="font-semibold text-gray-800">Sarah Johnson</p>
                <p className="text-sm text-gray-500">TechFlow Solutions</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Our Data Science Portfolio</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              See how we've helped businesses worldwide build successful data science solutions
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl hover:shadow-lg transition-shadow border border-gray-200">
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4">
                <Icon name="BarChart" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Business Intelligence Platform</h3>
              <p className="text-gray-600 mb-4">Built comprehensive BI platform with data integration, analytics, and interactive dashboards. Enabled data-driven decisions improving operational efficiency by 40%.</p>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">Tableau</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">Python</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">SQL</span>
              </div>
              <p className="text-sm text-gray-500">Client: Enterprise Solutions | Location: USA</p>
            </div>
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl hover:shadow-lg transition-shadow border border-gray-200">
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4">
                <Icon name="TrendingUp" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Statistical Analysis System</h3>
              <p className="text-gray-600 mb-4">Developed advanced statistical modeling system for market research analyzing customer behavior. Provided insights increasing marketing ROI by 60% and customer acquisition.</p>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">R</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">Statistical Modeling</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">A/B Testing</span>
              </div>
              <p className="text-sm text-gray-500">Client: MarketResearch Inc | Location: UK</p>
            </div>
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl hover:shadow-lg transition-shadow border border-gray-200">
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4">
                <Icon name="Database" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Data Warehouse Solution</h3>
              <p className="text-gray-600 mb-4">Created enterprise data warehouse consolidating data from 20+ sources. Enabled unified analytics improving reporting speed by 10x and data accuracy by 95%.</p>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">ETL</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">Data Warehouse</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">BigQuery</span>
              </div>
              <p className="text-sm text-gray-500">Client: GlobalTech Corp | Location: Canada</p>
            </div>
          </div>
        </div>
      </section>

      {/* Special Offers Section */}
      <section id="offers" className="py-12 sm:py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Special Offers & Free Trials</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Start your data science project with confidence - try our services risk-free
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="bg-[#00d690] text-white px-3 py-1 rounded-full text-sm font-medium">POPULAR</span>
              </div>
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6">
                <Icon name="Play" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">Free Trial</h3>
              <p className="text-gray-600 mb-6">Get 2 hours of free data science work to experience our quality</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>2 hours of free development</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>No commitment required</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Keep the insights you receive</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Full project consultation</span></li>
              </ul>
              <Button variant="primary" animation="bounce" className="w-full bg-[#00d690] text-white hover:bg-[#00b876]" onClick={async () => { await scrollToElement('#get-quote', { smooth: true, maxRetries: 15, retryDelay: 150 }).catch(() => {}); }}>Start Free Trial</Button>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="bg-[#00d690] text-white px-3 py-1 rounded-full text-sm font-medium">NEW</span>
              </div>
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6">
                <Icon name="Gift" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">Free Credits</h3>
              <p className="text-gray-600 mb-6">Get $500 in data science credits when you sign up</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>$500 free credits</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Valid for 30 days</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Deducted from final invoice</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Use on any project</span></li>
              </ul>
              <Button variant="primary" animation="pulse" className="w-full bg-[#00d690] text-white hover:bg-[#00b876]" onClick={async () => { await scrollToElement('#get-quote', { smooth: true, maxRetries: 15, retryDelay: 150 }).catch(() => {}); }}>Claim Free Credits</Button>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="bg-[#00d690] text-white px-3 py-1 rounded-full text-sm font-medium">GUARANTEE</span>
              </div>
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6">
                <Icon name="Shield" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">100% Satisfaction</h3>
              <p className="text-gray-600 mb-6">Not satisfied? Get your money back within 7 days</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>7-day money-back guarantee</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>No questions asked</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Keep all deliverables</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Full refund guarantee</span></li>
              </ul>
              <Button variant="primary" animation="scale" className="w-full bg-[#00d690] text-white hover:bg-[#00b876]" onClick={async () => { await scrollToElement('#get-quote', { smooth: true, maxRetries: 15, retryDelay: 150 }).catch(() => {}); }}>Start Risk-Free</Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators Section */}
      <section id="trust" className="py-12 bg-white border-t border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 sm:gap-12">
            <div className="flex items-center gap-3">
              <Icon name="Shield" size={24} className="text-[#00d690]" />
              <span className="text-sm sm:text-base font-medium text-gray-700">ISO Certified</span>
            </div>
            <div className="flex items-center gap-3">
              <Icon name="Lock" size={24} className="text-[#00d690]" />
              <span className="text-sm sm:text-base font-medium text-gray-700">GDPR Compliant</span>
            </div>
            <div className="flex items-center gap-3">
              <Icon name="FileText" size={24} className="text-[#00d690]" />
              <span className="text-sm sm:text-base font-medium text-gray-700">NDA Protected</span>
            </div>
            <div className="flex items-center gap-3">
              <Icon name="CheckCircle" size={24} className="text-[#00d690]" />
              <span className="text-sm sm:text-base font-medium text-gray-700">Money-Back Guarantee</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="get-quote" className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-[#f4faf8] via-white to-[#e8f5e8]">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-block bg-[#00d690]/10 text-[#00d690] px-4 py-2 rounded-full text-sm font-medium mb-6">
              Start Your Data Science Project Today
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 text-gray-800">Ready to Unlock Insights From Your Data?</h2>
            <p className="text-lg md:text-xl mb-8 text-gray-600">
              Get a free consultation and detailed quote for your data science project.
            </p>
            
            <div className="bg-white rounded-2xl p-6 md:p-8 max-w-2xl mx-auto shadow-xl border border-gray-100">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                    placeholder="Your Name"
                    required
                  />
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                    placeholder="Your Email"
                    required
                  />
                </div>
                <input 
                  type="tel" 
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                  placeholder="Your Phone"
                  required
                />
                <textarea 
                  name="requirements"
                  value={formData.requirements}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                  placeholder="Describe your data science requirements..."
                  required
                ></textarea>
                
                {submitMessage && (
                  <div className={`p-4 rounded-lg text-center ${
                    submitMessage.includes('Thank you') 
                      ? 'bg-green-100 text-green-700 border border-green-200' 
                      : 'bg-[#00d690]/10 text-red-700 border border-red-200'
                  }`}>
                    {submitMessage}
                  </div>
                )}
                
                <Button 
                  type="submit"
                  variant="primary"
                  animation="shake"
                  className="w-full py-3 bg-[#00d690] text-white hover:bg-[#00b876] shadow-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Get Free Quote & Consultation'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ 
        faqs={dataScienceFAQs}
        title="Frequently Asked Questions"
        description="Common questions about our data science services."
      />

      <Footer />
    </div>
  );
};

export default DataScienceLanding;

